import { Group, Text, Menu } from '@mantine/core';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useState, useContext } from 'react';
import cn from 'clsx';
import { GiHamburgerMenu } from 'react-icons/gi';
import { RxCaretUp } from 'react-icons/rx';
import { FaArrowDown } from 'react-icons/fa6';
import { ConfigContext } from 'src/app/ConfigContext';
import { WalletSuspense } from 'src/components/WalletSuspense/WalletSuspense';
import { HEADER_LINKS } from 'src/constants';
import * as Icons from 'src/assets';
import { HeaderLink } from 'src/types';

export function LayoutHeader() {
  const location = useLocation();
  const { config } = useContext(ConfigContext);
  const HeaderLogo = Icons[config.headerLogo as keyof typeof Icons];

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // @ts-ignore-next-line
  const renderWalletBtn = () => <w3m-button size="sm" balance="hide" />;

  const handleMobileNavClick = () => {
    setMobileMenuOpen(false);
  };

  const isLinkActive = ({
    isActive,
    to,
    isPrecise,
  }: {
    isActive: boolean;
    to: string;
    isPrecise?: boolean;
  }) => {
    if (!isPrecise) {
      return isActive;
    }

    return isActive && location.search === to.split('/').slice(-1)[0];
  };

  const renderLinks = ({
    onClick,
    isMobileNav,
  }: { onClick?: () => void; isMobileNav?: boolean } = {}) => {
    return HEADER_LINKS.map((_link) => {
      const withSublinks = !!_link.subLinks?.length;

      const renderLink = (link: HeaderLink) => {
        return (
          <NavLink
            key={link.to}
            to={link.to}
            end
            className={({ isActive }) =>
              cn(
                'relative flex items-center px-2 py-1',
                isLinkActive({
                  isActive,
                  to: link.to,
                  isPrecise: link.usePreciseSearchParamsMatch,
                })
                  ? 'link-active'
                  : undefined
              )
            }
            target={!link.to.startsWith('/') ? '_blank' : undefined}
            onClick={() => onClick?.()}
          >
            {({ isActive }) => {
              const _isActive = isLinkActive({
                isActive,
                to: link.to,
                isPrecise: link.usePreciseSearchParamsMatch,
              });

              return (
                <Group gap={2}>
                  <Text
                    fz={16}
                    fw={600}
                    lh={1.25}
                    variant={_isActive ? 'gradient' : undefined}
                    c={_isActive ? undefined : '#7E7E95'}
                  >
                    {link.label}
                  </Text>
                  {!isMobileNav && withSublinks && (
                    <RxCaretUp
                      size={24}
                      color="var(--mantine-color-brand-0)"
                      className="rotate-180"
                    />
                  )}
                </Group>
              );
            }}
          </NavLink>
        );
      };

      if (withSublinks) {
        if (isMobileNav) {
          return _link.subLinks?.map((subLink) => renderLink(subLink));
        }

        return (
          <Menu
            key={_link.to}
            trigger="hover"
            classNames={{
              dropdown:
                'overflow-hidden bg-[#151519] rounded-[16px] border-[#202027] p-0',
              item: 'p-3 [a:not(:last-child)>&]:border-b-[1px] border-[#202027] border-solid',
              itemLabel: 'text-[#A0A0B1] font-[500] text-[16px]',
            }}
          >
            <Menu.Target>{renderLink(_link)}</Menu.Target>
            <Menu.Dropdown>
              {_link.subLinks?.map(({ to, label, icon }) => (
                <Link to={to} key={to}>
                  <Menu.Item>
                    <Group gap={6}>
                      {icon}
                      {label}
                    </Group>
                  </Menu.Item>
                </Link>
              ))}
            </Menu.Dropdown>
          </Menu>
        );
      }

      return renderLink(_link);
    });
  };

  return (
    <>
      <Group h="100%" justify="space-between" wrap="nowrap" gap={4}>
        <Group h="100%" gap={48} wrap="nowrap">
          <Group className="gap-1 md:gap-4" wrap="nowrap">
            <Link to="/">
              <HeaderLogo className="h-5 w-auto md:h-6" />
            </Link>
          </Group>

          <Group h="100%" gap={24} className="max-lg:hidden" wrap="nowrap">
            {renderLinks()}
          </Group>
        </Group>
        <Group className="gap-1 md:gap-4">
          <Group gap={16}>
            <WalletSuspense withGradientBorderBtn>
              <Group gap={16} className="hidden lg:block">
                {renderWalletBtn()}
              </Group>
            </WalletSuspense>
          </Group>
          <GiHamburgerMenu
            size={24}
            className="lg:hidden cursor-pointer"
            color="var(--mantine-color-main-1)"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          />
        </Group>
      </Group>
      <div
        className={cn(
          'lg:hidden absolute z-50 top-[100%] left-0 w-full h-[100dvh] bg-brandblack px-[4%] py-4 flex flex-col gap-6 items-end',
          'translate-x-[100%] transition-transform duration-300'
        )}
        style={
          {
            ...(mobileMenuOpen ? { '--tw-translate-x': '0%' } : {}),
          } as React.CSSProperties
        }
      >
        {renderWalletBtn()}
        {renderLinks({ onClick: handleMobileNavClick, isMobileNav: true })}
      </div>
    </>
  );
}
